import { useSearchParams } from 'react-router-dom'
import { CheckCircle2, Download, ExternalLink, Monitor, Smartphone } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { CopyButton } from '@/components/shared/CopyButton'
import { AuthAlert } from '@/components/auth/AuthAlert'
import { WEBMAIL_URL } from '@/lib/constants'

const MAIL_HOST = 'mail.purpletoadmail.com'

function SettingRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid min-w-0 gap-1 rounded-lg bg-accent/40 px-3 py-2 sm:grid-cols-[8rem_minmax(0,1fr)_auto] sm:items-center sm:gap-3">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="min-w-0 break-all font-mono text-sm">{value}</span>
      <CopyButton text={value} label={label} className="justify-self-end" />
    </div>
  )
}

export function ClientSetup() {
  const [searchParams] = useSearchParams()
  const email = (searchParams.get('email') || '').trim()
  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  const mobileConfigUrl = `/.well-known/mail/mobileconfig?email=${encodeURIComponent(email)}`
  const webmailUrl = `${WEBMAIL_URL}/login?email=${encodeURIComponent(email)}`

  if (!validEmail) {
    return (
      <div className="space-y-4">
        <p className="pt-eyebrow">Mailbox setup</p>
        <h1 className="pt-page-title">Set up your mail app.</h1>
        <AuthAlert message="This setup link is missing a valid mailbox email address. Ask your account administrator for a new link." />
      </div>
    )
  }

  return (
    <main className="mx-auto w-full max-w-5xl space-y-6 pb-6" aria-labelledby="client-setup-title">
      <section className="pt-surface overflow-hidden rounded-2xl p-5 sm:p-8">
        <div className="pt-page-header border-b border-border/80 pb-6">
        <div>
          <p className="pt-eyebrow">Mailbox setup</p>
          <h1 id="client-setup-title" className="pt-page-title mt-2">Set up your mail app.</h1>
          <p className="pt-page-description">Set up <span className="break-all font-medium text-foreground">{email}</span> in webmail, Apple Mail, Thunderbird, or another standard email app.</p>
        </div>
        <Button asChild variant="outline" className="shrink-0">
          <a href={webmailUrl}>
            Open Webmail
            <ExternalLink className="ml-2 h-4 w-4" />
          </a>
        </Button>
      </div>

        <div className="mt-6 grid gap-4 md:grid-cols-2">
        <Card className="border-primary/25 bg-primary/[0.055] shadow-none">
          <CardContent className="flex h-full flex-col space-y-5 p-5 sm:p-6">
          <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10"><Smartphone className="h-4 w-4 text-primary" /></div>
          <div>
            <h2 className="font-semibold tracking-[-0.015em]">Apple Mail</h2>
            <p className="mt-1 text-sm leading-6 text-muted-foreground">Download the profile, then review and install it in Settings on iPhone, iPad, or Mac.</p>
          </div>
          </div>
          <Button asChild className="mt-auto w-full sm:w-fit">
          <a href={mobileConfigUrl} download>
            <Download className="mr-2 h-4 w-4" />
            Download Apple profile
          </a>
        </Button>
        </CardContent>
      </Card>

        <Card className="shadow-none">
          <CardContent className="p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-muted"><Monitor className="h-4 w-4 text-foreground" /></div>
          <div>
            <h2 className="font-semibold tracking-[-0.015em]">Thunderbird and other apps</h2>
            <p className="mt-1 text-sm leading-6 text-muted-foreground">Choose manual setup or “Other account.” Use your mailbox password and enable authentication for outgoing mail.</p>
          </div>
        </div>
        </CardContent>
      </Card>
        </div>
      </section>

      <section className="pt-surface space-y-4 rounded-2xl p-5 sm:p-6" aria-labelledby="manual-settings-title">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-emerald-400" />
          <h2 id="manual-settings-title" className="text-sm font-semibold">Manual settings</h2>
        </div>
        <SettingRow label="Email / username" value={email} />
        <SettingRow label="IMAP server" value={MAIL_HOST} />
        <SettingRow label="IMAP port" value="993" />
        <SettingRow label="IMAP security" value="SSL/TLS" />
        <SettingRow label="SMTP server" value={MAIL_HOST} />
        <SettingRow label="SMTP port" value="587" />
        <SettingRow label="SMTP security" value="STARTTLS" />
      </section>
    </main>
  )
}
